import { useCallback, useState } from "react";

type Validator<TState> = (state: TState) => string | undefined;

export const useFieldValidation = <TState>(
  validators: Partial<Record<string, Validator<TState>>>
) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateFields = useCallback(
    (state: TState, fieldNames?: string[]) => {
      const targetFields =
        fieldNames && fieldNames.length > 0
          ? fieldNames
          : Object.keys(validators);

      return targetFields.reduce<Record<string, string>>((acc, field) => {
        const validator = validators[field];
        if (!validator) {
          return acc;
        }

        const error = validator(state);
        if (error) {
          acc[field] = error;
        }

        return acc;
      }, {});
    },
    [validators]
  );

  const validateAll = useCallback(
    (state: TState) => {
      const validationResult = validateFields(state, Object.keys(validators));
      setErrors(validationResult);
      return validationResult;
    },
    [validateFields]
  );

  const revalidateFields = useCallback(
    (state: TState, fieldNames: string[]) => {
      if (!fieldNames.length) return;

      const fieldErrors = validateFields(state, fieldNames);
      setErrors((prev) => {
        let updated = false;
        const next = { ...prev };
        fieldNames.forEach((field) => {
          if (!Object.prototype.hasOwnProperty.call(prev, field)) {
            return;
          }
          if (fieldErrors[field]) {
            if (next[field] !== fieldErrors[field]) {
              next[field] = fieldErrors[field]!;
              updated = true;
            }
          } else {
            delete next[field];
            updated = true;
          }
        });
        return updated ? next : prev;
      });
    },
    [validateFields]
  );

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const resetErrors = useCallback(() => setErrors({}), []);

  return {
    errors,
    setErrors,
    validateAll,
    revalidateFields,
    clearFieldError,
    resetErrors,
  };
};

